import { Component, computed, effect, inject, Inject, signal } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogClose, MatDialog } from "@angular/material/dialog";
import { IDropdownSettings, NgMultiSelectDropDownModule } from "ng-multiselect-dropdown";
import { FulfilmentRequest } from "../../../helpers/self-service/models/types";
import { ApiHelper } from "../../../helpers/apihelper";
import { AuthService } from "../../../services/AuthService";
import { WIRELESS_SUBSCRIPTIONS_LOCATIONS_QUERY } from "./dialog-incident.rgql";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";
import { AngularSvgIconModule } from "angular-svg-icon";
import { FormsModule } from "@angular/forms";
import { DialogIncidentViewModel } from "./dialog-incident.model";
import { Apollo } from "apollo-angular";
import { WirelessListQueryQuery } from "../../../gql/generated-ingestor";

@Component({
  selector: "dialog-incident",
  templateUrl: "dialog-incident.html",
  styleUrls: ["dialog-incident.scss"],
  imports: [
    CommonModule,
    AngularSvgIconModule,
    TranslateModule,
    FormsModule,
    NgMultiSelectDropDownModule,
    MatDialogClose,
  ],
})
export class DialogIncidentComponent {
  private readonly apollo = inject(Apollo);
  private readonly dialogRef = inject(MatDialogRef<DialogIncidentComponent>);
  private readonly api = inject(ApiHelper);
  private readonly auth = inject(AuthService);
  private readonly translate = inject(TranslateService);

  protected dropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: "item_id",
    textField: "item_text",
    selectAllText: "Select All",
    unSelectAllText: "Unselect All",
    itemsShowLimit: 1,
    allowSearchFilter: true,
    defaultOpen: false,
  };

  public __fulfilmentRequest: any;

  protected dropdownLoaded = signal(false);

  public subscription = signal<DialogIncidentViewModel>(undefined);
  private subscriptionId = computed(() => this.subscription().subscriptionId);
  private product = computed(() => this.subscription().product);
  protected isWireless = computed(() => this.product().type === "Wireless");
  protected presentableName = computed(() => this._presentableName(this.subscription()));

  protected dropdownList = [];
  protected selectedItems = [];

  protected errorMessage = "";
  protected successMessage = "";

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { subscription: DialogIncidentViewModel },
  ) {
    this.subscription.set(data.subscription);
    this.__fulfilmentRequest = {};
    this.__fulfilmentRequest.fullName = this.auth.currentUser().displayName;
    this.__fulfilmentRequest.email = this.auth.currentUser().email;

    this.selectedItems = [{ item_id: 1, item_text: "Locatie 1" }];

    effect(() => {
      if (this.isWireless()) {
        this.fetchWirelessLocations();
        this.dropdownLoaded.set(true);
      }
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  fetchWirelessLocations() {
    this.apollo
      .use("replicatedData")
      .query<WirelessListQueryQuery>({
        query: WIRELESS_SUBSCRIPTIONS_LOCATIONS_QUERY,
        variables: { customer: localStorage.getItem("viewingCustomerGUID") },
      })
      .subscribe(({ data }) => {
        const subscriptions = data.subscriptions.page;

        this.dropdownList = subscriptions
          .map((subscription) => {
            const item_text = subscription.location.city + " - " + this._presentableName(subscription);
            const item_id = subscription.subscriptionId;

            return { item_id, item_text };
          })
          .sort((a, b) => a.item_text.localeCompare(b.item_text));

        this.selectedItems = this.dropdownList.filter(({ item_id }) => item_id === this.subscriptionId());
      });
  }

  private _presentableName(subscription: { customerDescription: string; description: string }) {
    return subscription.customerDescription || subscription.description;
  }

  notifySupport(): void {
    this.successMessage = "";
    this.errorMessage = "";

    if (!this.__fulfilmentRequest.text || this.__fulfilmentRequest.text.length < 10) {
      this.errorMessage = this.translate.instant("Selfservice.InvalidDescription");
      return;
    }

    if (!this.__fulfilmentRequest.fullName || this.__fulfilmentRequest.fullName.length < 5) {
      this.errorMessage = this.translate.instant("Selfservice.InvalidFullname");
      return;
    }

    if (!this.__fulfilmentRequest.phone || this.__fulfilmentRequest.phone.length < 5) {
      this.errorMessage = this.translate.instant("Selfservice.InvalidPhone");
      return;
    }

    const customerId = localStorage.getItem("viewingCustomerGUID");
    const locationText = this.selectedItems.map((item) => `- ${item.item_text}`).join("\n");

    const requestMessage = [
      this.__fulfilmentRequest.text,
      `${this.translate.instant("Selfservice.Locations")}: `,
      locationText,
    ].join("\n\n");

    const request: FulfilmentRequest = {
      contacts: [
        {
          fullName: this.__fulfilmentRequest.fullName,
          email: this.__fulfilmentRequest.email,
          phone: this.__fulfilmentRequest.phone,
        },
      ],
      serviceRequest: "problem",
      text: requestMessage,
      subscriptionId: this.subscriptionId(),
      product: this.product().type,
    };

    this.api
      .add_new_fulfilment_problem(customerId, JSON.stringify(request))
      .then((result) => {
        this.successMessage = this.translate.instant("Selfservice.NotificationSent");
      })
      .catch((err) => {
        this.errorMessage = err;
      });
  }
}

/**
 * Function to help instantiating DialogIncidentComponent with the right input data.
 *
 * Prefer calling this function over directly calling `dialog.open(DialogIncidentComponent, data)`
 * because the latter doesn't help enforce that `data` is of type DialogIncidentModel.
 *
 * @param dialog A mat dialog reference with which to open the incident dialog
 * @param subscription The data to populate the incident dialog with
 * @returns The `MatDialogRef` returned by `dialog.open`
 */
export function openDialogIncident(dialog: MatDialog, subscription: DialogIncidentViewModel) {
  const data = { data: { subscription } };
  return dialog.open(DialogIncidentComponent, data);
}
